import React, { useMemo } from 'react';
import Link from 'next/link';
import Layout from '../components/layout';
import Head from 'next/head';
import Container from '../components/container';
import NavBar from '../components/navbar';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { H2, H5 } from '../components/text';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { ITeam } from '../models/team';
import { IPage } from '../models/page';
import filterTeams from '../utils/filterHistoricalTeams';

interface IProps {
	primaryMenuItems: IMenuItem[];
	pageData: IPage;
	sponsors: ISponsor[];
	teams: ITeam[];
}

export default function TeamsPage({ primaryMenuItems, pageData: { page }, sponsors, teams }: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<article className="mb-8">
					<H2 className="text-center py-6">{page.title}</H2>
					<section className="mb-12">
						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
							{teams.map((team) => (
								<div key={team.slug}>
									<Link href={`/teams/${team.slug}`}>
										<div className="bg-white p-8 rounded-lg shadow-md flex justify-center flex-col items-center hover:shadow-lg hover:opacity-70 transition-opacity">
											<img
												src={team?.team_logo}
												alt={team.title.rendered}
												className="h-32 w-32 object-contain"
											/>
											<H5>{team.title.rendered}</H5>
										</div>
									</Link>
								</div>
							))}
						</div>
					</section>
				</article>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(
		`
		query {
			page(id: 80875, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	// Fetch data for all the teams
	const response = await fetch(`${config.apiUrl}/wp-json/wp/v2/teams?per_page=40&order=asc&orderby=title&_fields=id,slug,title,team_logo`);
	const teams: ITeam[] = await response.json();

	const filteredTeams = filterTeams(teams);

	return {
		revalidate: 3600,
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			teams: filteredTeams,
		},
	};
};
